
export const PAGE_SIZES: readonly number[] = [25, 50, 100]

export const PAGE_SIZE_NOTICE = 'Page size must be 25, 50 or 100'

export interface ParsedValue<T> {
  value: T
  notice: string | null
}

export const parsePageSize = (raw: string | null | undefined, defaultSize: number): ParsedValue<number> => {
  if (raw == null || raw === '') return { value: defaultSize, notice: null }
  const n = Number.parseInt(raw, 10)
  if (Number.isInteger(n) && PAGE_SIZES.includes(n) && String(n) === raw.trim()) {
    return { value: n, notice: null }
  }
  return { value: defaultSize, notice: PAGE_SIZE_NOTICE }
}

export const parseChoice = (
  raw: string | null | undefined,
  allowed: readonly string[],
  defaultValue: string,
  label: string
): ParsedValue<string> => {
  if (raw == null || raw === '') return { value: defaultValue, notice: null }
  if (allowed.includes(raw)) return { value: raw, notice: null }
  return { value: defaultValue, notice: `${label} must be one of: ${allowed.join(', ')}` }
}

export const parseFlag = (raw: string | null | undefined): boolean => raw === '1'

export const resetTokens = (): string[] => []

export const pushToken = (stack: readonly string[], nextToken: string): string[] =>
  [...stack, nextToken]

export const popToken = (stack: readonly string[]): string[] =>
  stack.slice(0, Math.max(0, stack.length - 1))

export const pageNumberOf = (stack: readonly string[]): number => stack.length + 1

export const tokenForPage = (stack: readonly string[]): string | null =>
  stack.length === 0 ? null : stack[stack.length - 1]

export interface RequestRace {
  begin: () => number
  isCurrent: (id: number) => boolean
}

export const createRequestRace = (): RequestRace => {
  let latest = 0
  return {
    begin: () => {
      latest += 1
      return latest
    },
    isCurrent: (id: number) => id === latest
  }
}

export interface PagerGate {
  drop: () => void
  hold: () => void
  settle: (nextToken: string | null) => void
  fail: () => void
  takeNext: () => string | null
  takePrev: () => boolean
}

export const createPagerGate = (): PagerGate => {
  let held = false
  let cursor: string | null = null
  return {
    drop: () => {
      held = true
      cursor = null
    },
    hold: () => {
      held = true
    },
    settle: (nextToken: string | null) => {
      held = false
      cursor = nextToken
    },
    fail: () => {
      held = false
    },
    takeNext: () => {
      if (held || cursor == null) return null
      const token = cursor
      held = true
      cursor = null
      return token
    },
    takePrev: () => {
      if (held) return false
      held = true
      cursor = null
      return true
    }
  }
}

export interface BroadcasterGroup<T> {
  broadcasterUserId: string
  rows: T[]
}

export const groupByBroadcaster = <T extends { broadcasterUserId: string }>(
  rows: readonly T[]
): Array<BroadcasterGroup<T>> => {
  const order: string[] = []
  const byId = new Map<string, T[]>()
  for (const row of rows) {
    const bucket = byId.get(row.broadcasterUserId)
    if (bucket == null) {
      order.push(row.broadcasterUserId)
      byId.set(row.broadcasterUserId, [row])
    } else {
      bucket.push(row)
    }
  }
  return order.map((broadcasterUserId) => ({
    broadcasterUserId,
    rows: byId.get(broadcasterUserId) ?? []
  }))
}
